import { Injectable } from '@angular/core';
import { Observable, catchError, of, switchMap, tap } from 'rxjs';
import { Store } from '../../../../../services/store';
import { toVoid } from '../../../../../utils/void';
import { BomAttachmentsProvider } from './bom-attachments.provider';
import { BomAttachmentDto } from './bom-attachment.dto';

// Aligned with the API's limit (Kestrel's default MaxRequestBodySize) — the API would
// answer an oversized request with a bare 413, so the friendly check happens here
export const MAX_ATTACHMENT_SIZE_BYTES = 30_000_000;

export interface BomAttachmentsState {
  loading: boolean;
  attachments: BomAttachmentDto[] | undefined;
  uploading: boolean;
  uploadError: string | undefined;
}

const INITIAL_STATE: BomAttachmentsState = {
  loading: false,
  attachments: undefined,
  uploading: false,
  uploadError: undefined
};

@Injectable({ providedIn: 'root' })
export class BomAttachmentsStore extends Store<BomAttachmentsState> {
  constructor(private provider: BomAttachmentsProvider) {
    super(INITIAL_STATE);
  }

  init(bomItemId: number | undefined): Observable<void> {
    if (bomItemId === undefined) {
      // item not saved in backend yet (/bom/new) — nothing can be attached to it
      this.update(state => ({ ...state, loading: false, attachments: [], uploadError: undefined }));
      return of(undefined);
    }

    this.update(state => ({ ...state, loading: true, uploadError: undefined }));

    return this.provider.getAttachments(bomItemId)
      .pipe(
        tap(attachments => {
          this.update(state => ({
            ...state,
            loading: false,
            attachments
          }));
        }),
        toVoid()
      );
  }

  upload(bomItemId: number, file: File): Observable<void> {
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      this.update(state => ({ ...state, uploadError: 'Plik przekracza limit 30 MB' }));
      return of(undefined);
    }

    this.update(state => ({ ...state, uploading: true, uploadError: undefined }));

    return this.provider.uploadAttachment(bomItemId, file)
      .pipe(
        tap(() => this.update(state => ({ ...state, uploading: false }))),
        switchMap(() => this.init(bomItemId)),
        catchError(error => {
          this.update(state => ({
            ...state,
            uploading: false,
            uploadError: typeof error.error === 'string' ? error.error : 'Nie udało się wysłać pliku'
          }));
          return of(undefined);
        })
      );
  }

  softDelete(bomItemId: number, attachmentId: number): Observable<void> {
    return this.provider.deleteAttachment(attachmentId)
      .pipe(switchMap(() => this.init(bomItemId)));
  }

  setPrimaryPhoto(bomItemId: number, attachmentId: number): Observable<void> {
    return this.provider.setPrimaryPhoto(attachmentId)
      .pipe(switchMap(() => this.init(bomItemId)));
  }

  contentUrl(attachmentId: number): string {
    return this.provider.contentUrl(attachmentId);
  }
}
